import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from 'react';
export type ViewLayout = 'grid' | 'carousel';
interface ViewPreferences {
  layout: ViewLayout;
  zoom: number;
  soundEnabled: boolean;
}
const DEFAULT_PREFERENCES: ViewPreferences = {
  layout: 'grid',
  zoom: 100,
  soundEnabled: false
};

// Versioned key so the schema can evolve safely. Bump the suffix when the shape
// changes; the legacy key is read once for migration, then cleaned up.
const STORAGE_KEY = 'closet-preferences-v1';
const LEGACY_STORAGE_KEY = 'closet-preferences';
const PREFERENCES_EVENT = 'closet-preferences-change';
let cachedStorageValue: string | null | undefined;
let cachedPreferences = DEFAULT_PREFERENCES;
function parsePreferences(raw: string | null): Partial<ViewPreferences> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    // Only accept plain objects; ignore corrupt/legacy values.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Partial<ViewPreferences>;
    }
  } catch {
    // Invalid stored data
  }
  return null;
}

// Read persisted prefs from localStorage for the client-side external store.
function getStoredPreferencesValue(): string | null {
  const currentValue = localStorage.getItem(STORAGE_KEY);
  if (currentValue) return currentValue;

  // One-time migration from the unversioned key.
  const legacy = parsePreferences(localStorage.getItem(LEGACY_STORAGE_KEY));
  if (legacy) {
    const migrated = {
      ...DEFAULT_PREFERENCES,
      ...legacy
    };
    const migratedValue = JSON.stringify(migrated);
    try {
      localStorage.setItem(STORAGE_KEY, migratedValue);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore write failures (private mode, quota)
    }
    return migratedValue;
  }
  return null;
}
function getPreferencesSnapshot(): ViewPreferences {
  const storageValue = getStoredPreferencesValue();
  if (storageValue === cachedStorageValue) return cachedPreferences;
  cachedStorageValue = storageValue;
  cachedPreferences = {
    ...DEFAULT_PREFERENCES,
    ...parsePreferences(storageValue)
  };
  if (cachedPreferences.layout !== 'carousel') cachedPreferences.layout = 'grid';
  return cachedPreferences;
}
function getServerPreferencesSnapshot(): ViewPreferences {
  return DEFAULT_PREFERENCES;
}
function subscribeToPreferences(onStoreChange: () => void) {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === LEGACY_STORAGE_KEY) {
      cachedStorageValue = undefined;
      onStoreChange();
    }
  };
  window.addEventListener('storage', handleStorage);
  window.addEventListener(PREFERENCES_EVENT, onStoreChange);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(PREFERENCES_EVENT, onStoreChange);
  };
}
function updatePreferences(update: (current: ViewPreferences) => ViewPreferences) {
  const preferences = update(getPreferencesSnapshot());
  const storageValue = JSON.stringify(preferences);
  localStorage.setItem(STORAGE_KEY, storageValue);
  cachedStorageValue = storageValue;
  cachedPreferences = preferences;
  window.dispatchEvent(new Event(PREFERENCES_EVENT));
}
function setLayout(layout: ViewLayout) {
  updatePreferences(current => ({
    ...current,
    layout
  }));
}
function setZoom(zoom: number) {
  updatePreferences(current => ({
    ...current,
    zoom
  }));
}
function setSoundEnabled(soundEnabled: boolean) {
  updatePreferences(current => ({
    ...current,
    soundEnabled
  }));
}
export function useViewPreferences() {
  const $ = _c(2);
  const preferences = useSyncExternalStore(subscribeToPreferences, getPreferencesSnapshot, getServerPreferencesSnapshot);
  let t0;
  if ($[0] !== preferences) {
    t0 = {
      ...preferences,
      setLayout,
      setZoom,
      setSoundEnabled
    };
    $[0] = preferences;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}